import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, subirArchivo } from "../lib/api";
import { euros, fecha, fechaHora, plural } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import BloqueoInactividad from "../componentes/BloqueoInactividad";
import { useSesionPlataforma } from "./sesionPlataforma";
import Licencias from "./Licencias";
import Solicitudes from "./Solicitudes";
import { CobrosDelClub, EquipoDelClub } from "./SoporteClub";

interface Club {
  id: string;
  nombre: string;
  slug: string;
  ciudad: string | null;
  email: string | null;
  telefono: string | null;
  activo: boolean;
  createdAt: string;
  pruebaHasta: string | null;
  cuotaMensualCentimos: number;
  notasPlataforma: string | null;
  motivoSuspension: string | null;
  pagadoHasta: string | null;
  estado: "PRUEBA" | "PRUEBA_CADUCADA" | "AL_DIA" | "MOROSO" | "SUSPENDIDO";
  sociosTotales: number;
  sociosActivos: number;
  usuarios: number;
  entregas30Dias: number;
  ultimaActividad: string | null;
  /** Que tramo de la tarifa le toca por tamaño, y si la cuota cuadra. */
  plan: {
    tramo: string;
    precioQueTocaCentimos: number | null;
    desajuste: "COBRA_DE_MENOS" | "COBRA_DE_MAS" | "SIN_TARIFA" | null;
    diferenciaCentimos: number;
  };
}

interface Respuesta {
  clubes: Club[];
  /** Los días de prueba que anuncia la web: lo que propone el formulario de club nuevo. */
  diasDePrueba: number;
  /** Solicitudes de prueba sin contestar, para el botón de arriba. */
  solicitudesPendientes: number;
  resumen: {
    total: number;
    activos: number;
    enPrueba: number;
    pruebaCaducada: number;
    morosos: number;
    suspendidos: number;
    pendienteDeCobroCentimos: number;
    ingresoMensualCentimos: number;
    sociosEnLaPlataforma: number;
    planCorto: number;
    porSubirCentimos: number;
  };
}

const ETIQUETA_ESTADO: Record<Club["estado"], { texto: string; clase: string }> = {
  AL_DIA: { texto: "Al día", clase: "verde" },
  PRUEBA: { texto: "En prueba", clase: "azul" },
  PRUEBA_CADUCADA: { texto: "Prueba caducada", clase: "ambar" },
  MOROSO: { texto: "Sin pagar", clase: "roja" },
  SUSPENDIDO: { texto: "Suspendido", clase: "gris" },
};

/** Panel de quien vende el programa: sus clubes, su uso y su suscripcion. */
export default function PlataformaApp() {
  const { admin, cargando, salir } = useSesionPlataforma();
  const clienteConsultas = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [vista, setVista] = useState<"clubes" | "licencias" | "solicitudes" | "registro">("clubes");
  const [mirandoCorreo, setMirandoCorreo] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<"" | Club["estado"]>("");
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  /*
   * Del club abierto se guarda el identificador, no una copia.
   *
   * Con la copia, el dialogo se quedaba congelado en como estaba el club al
   * abrirlo: apuntabas un cobro y seguia diciendo «todavia no se le ha cobrado
   * nada», o lo reactivabas y seguia poniendo «suspendido». Mirando la lista
   * de verdad, cada refresco se ve dentro. Y si el club desaparece -porque
   * acabas de borrarlo- el dialogo se cierra solo, que es lo que toca.
   */
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const datos = useQuery({
    queryKey: ["plataforma-clubes"],
    queryFn: () => api<Respuesta>("/plataforma/clubes"),
    enabled: Boolean(admin),
  });

  const refrescar = () => void clienteConsultas.invalidateQueries({ queryKey: ["plataforma-clubes"] });

  // Buscar por nombre o ciudad, sin distinguir mayusculas ni acentos: quien
  // escribe deprisa pone "asociacion" y el club se llama "Asociación".
  const sinAcentos = (t: string) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const texto = sinAcentos(busqueda.trim());
  /** El club abierto, leido de la lista fresca en cada repintado. */
  const detalle = detalleId ? ((datos.data?.clubes ?? []).find((c) => c.id === detalleId) ?? null) : null;

  const visibles = (datos.data?.clubes ?? []).filter(
    (c) =>
      (!filtro || c.estado === filtro) &&
      (!texto || sinAcentos(`${c.nombre} ${c.ciudad ?? ""} ${c.slug}`).includes(texto)),
  );

  if (cargando) return <div className="cargando">Cargando...</div>;
  if (!admin) return <Entrada />;

  return (
    <div className="plataforma">
      <Cabecera titulo="Plataforma" descripcion={`Sesión de ${admin.nombre}`}>
        <button
          className={`boton ${vista === "clubes" ? "" : "secundario"}`}
          onClick={() => setVista("clubes")}
        >
          Clubes
        </button>
        <button
          className={`boton ${vista === "licencias" ? "" : "secundario"}`}
          onClick={() => setVista("licencias")}
        >
          Licencias
        </button>
        <button
          className={`boton ${vista === "solicitudes" ? "" : "secundario"}`}
          onClick={() => setVista("solicitudes")}
        >
          Solicitudes{datos.data && datos.data.solicitudesPendientes > 0 ? ` (${datos.data.solicitudesPendientes})` : ""}
        </button>
        <button
          className={`boton ${vista === "registro" ? "" : "secundario"}`}
          onClick={() => setVista("registro")}
        >
          Registro
        </button>
        <button className="boton secundario" onClick={() => setCreando(true)}>
          + Club
        </button>
        <button className="boton secundario" onClick={() => setRestaurando(true)}>
          Restaurar
        </button>
        <button className="boton secundario" onClick={() => setMirandoCorreo(true)}>
          Correo
        </button>
        <button className="boton secundario" onClick={() => setCambiandoPassword(true)}>
          Contraseña
        </button>
        <button className="boton secundario" onClick={() => void salir()}>
          Salir
        </button>
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      {vista === "registro" ? (
        <Registro />
      ) : vista === "licencias" ? (
        <Licencias />
      ) : vista === "solicitudes" ? (
        <Solicitudes />
      ) : datos.isLoading || !datos.data ? (
        <Cargando />
      ) : (
        <>
          <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
            <TarjetaDato
              etiqueta="Ingreso mensual"
              valor={euros(datos.data.resumen.ingresoMensualCentimos)}
              pie={`${datos.data.resumen.activos} ${datos.data.resumen.activos === 1 ? "club activo" : "clubes activos"}`}
            />
            <TarjetaDato
              etiqueta="Clubes"
              valor={datos.data.resumen.total}
              pie={`${datos.data.resumen.enPrueba} en prueba · ${datos.data.resumen.suspendidos} suspendidos`}
            />
            <TarjetaDato
              etiqueta="Sin pagar"
              valor={datos.data.resumen.morosos}
              color={datos.data.resumen.morosos > 0 ? "var(--rojo)" : undefined}
              pie={
                datos.data.resumen.morosos > 0
                  ? `${euros(datos.data.resumen.pendienteDeCobroCentimos)}/mes sin cobrar`
                  : "Todos al corriente"
              }
            />
            <TarjetaDato
              etiqueta="Pruebas terminadas"
              valor={datos.data.resumen.pruebaCaducada}
              color={datos.data.resumen.pruebaCaducada > 0 ? "var(--ambar)" : undefined}
              pie="Pendientes de cobrar o cerrar"
            />
            <TarjetaDato
              etiqueta="Socios en la plataforma"
              valor={datos.data.resumen.sociosEnLaPlataforma}
              pie="Suma de todos los clubes"
            />
          </div>

          {/*
            Un club que crece no avisa. Aquí se ve el día que se pasa de tramo,
            que es cuando se puede llamar y subirle el plan sin reclamar meses
            atrás. Al club no se le bloquea nada: esto es un recordatorio tuyo.
          */}
          {datos.data.resumen.planCorto > 0 && (
            <Aviso tipo="atencion">
              {plural(datos.data.resumen.planCorto, "club", "clubes")}
              {datos.data.resumen.planCorto === 1 ? " paga" : " pagan"} menos de lo que marca su tramo
              {datos.data.resumen.porSubirCentimos > 0
                ? `: ${euros(datos.data.resumen.porSubirCentimos)}/mes de más que podrías estar cobrando. `
                : ". "}
              Míralos en la columna de cuota. A ellos no se les ha bloqueado nada.
            </Aviso>
          )}

          {datos.data.clubes.length > 5 && (
            <div className="fila" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre o ciudad..."
                style={{ flex: 1, minWidth: 200 }}
              />
              <select value={filtro} onChange={(e) => setFiltro(e.target.value as typeof filtro)}>
                <option value="">Todos los estados</option>
                {Object.entries(ETIQUETA_ESTADO).map(([clave, e]) => (
                  <option key={clave} value={clave}>
                    {e.texto}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Panel sinCuerpo>
            {visibles.length === 0 ? (
              <Vacio>
                {datos.data.clubes.length === 0
                  ? "Todavía no hay ningún club. Crea el primero con «+ Club», o emite una licencia para que se den de alta ellos."
                  : "Ningún club coincide con lo que buscas."}
              </Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Club</th>
                      <th>Estado</th>
                      <th className="num">Socios</th>
                      <th className="num">Entregas 30 d</th>
                      <th>Última actividad</th>
                      <th className="num">Cuota</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((c) => {
                      const estado = ETIQUETA_ESTADO[c.estado];
                      return (
                        <tr key={c.id} style={c.activo ? undefined : { opacity: 0.6 }}>
                          <td>
                            <strong>{c.nombre}</strong>
                            <div className="suave pequeno tabular">
                              {c.slug}
                              {c.ciudad ? ` · ${c.ciudad}` : ""}
                            </div>
                          </td>
                          <td>
                            <Etiqueta tipo={estado.clase}>{estado.texto}</Etiqueta>
                            {c.estado === "PRUEBA" && c.pruebaHasta && (
                              <div className="suave pequeno">hasta {fecha(c.pruebaHasta)}</div>
                            )}
                          </td>
                          <td className="num tabular">
                            {c.sociosActivos}
                            <div className="suave pequeno">de {c.sociosTotales}</div>
                          </td>
                          <td className="num tabular">{c.entregas30Dias}</td>
                          <td className="pequeno suave">
                            {c.ultimaActividad ? fechaHora(c.ultimaActividad) : "Sin actividad"}
                          </td>
                          <td className="num tabular">
                            {c.cuotaMensualCentimos > 0 ? `${euros(c.cuotaMensualCentimos)}/mes` : "—"}
                            {c.plan.desajuste && (
                              <div
                                className="pequeno"
                                style={{
                                  color: c.plan.desajuste === "COBRA_DE_MAS" ? "var(--suave)" : "var(--rojo)",
                                }}
                              >
                                {c.plan.precioQueTocaCentimos === null
                                  ? "sin tarifa"
                                  : `le tocan ${euros(c.plan.precioQueTocaCentimos)}`}
                              </div>
                            )}
                          </td>
                          <td>
                            <button className="boton fantasma" onClick={() => setDetalleId(c.id)}>
                              Gestionar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <p className="suave pequeno" style={{ marginTop: 12 }}>
            Este panel muestra recuentos, nunca datos de socios: quien mantiene la plataforma no necesita ver el
            documento ni el consumo de nadie.
          </p>
        </>
      )}

      {cambiandoPassword && (
        <DialogoPassword
          alCerrar={() => setCambiandoPassword(false)}
          alFallar={(m) => {
            setCambiandoPassword(false);
            setMensaje("");
            setError(m);
          }}
          alHecho={() => {
            setCambiandoPassword(false);
            setError("");
            setMensaje("Contraseña cambiada. Si la primera venia de una variable del servidor, borrala ya de ahí.");
          }}
        />
      )}

      {mirandoCorreo && <DialogoCorreo alCerrar={() => setMirandoCorreo(false)} />}

      {restaurando && (
        <DialogoRestaurar
          alCerrar={() => setRestaurando(false)}
          alHecho={(m) => {
            setRestaurando(false);
            setError("");
            setMensaje(m);
            refrescar();
          }}
        />
      )}

      {/* Aquí el bloqueo pesa más que en el club: esta contraseña no abre un
          club, los abre todos. Un portátil abierto en una cafetería basta. */}
      <BloqueoInactividad
        nombre={admin.nombre}
        subtitulo="Plataforma"
        comprobar={(password) =>
          api("/plataforma/auth/comprobar-password", { metodo: "POST", cuerpo: { password } })
        }
        alSalir={() => void salir()}
      />

      {creando && (
        <DialogoNuevoClub
          diasDePrueba={datos.data?.diasDePrueba ?? 0}
          alCerrar={() => setCreando(false)}
          alFallar={setError}
          alHecho={(nombre) => {
            setCreando(false);
            setError("");
            setMensaje(`Club «${nombre}» creado. Ya puede entrar con la cuenta del responsable.`);
            refrescar();
          }}
        />
      )}

      {detalle && (
        <DialogoClub
          club={detalle}
          alRefrescar={refrescar}
          alCerrar={() => setDetalleId(null)}
          alFallar={(m) => {
            setDetalleId(null);
            setMensaje("");
            setError(m);
          }}
          alHecho={(texto) => {
            setDetalleId(null);
            setError("");
            setMensaje(texto);
            refrescar();
          }}
        />
      )}
    </div>
  );
}

function Entrada() {
  const { entrar } = useSesionPlataforma();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  // Quien llega desde el correo trae el token en la dirección.
  const tokenReset = new URLSearchParams(window.location.search).get("token");
  const [recuperando, setRecuperando] = useState(false);

  if (tokenReset) return <PonerNueva token={tokenReset} />;
  if (recuperando) return <PedirEnlace alVolver={() => setRecuperando(false)} />;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await entrar(email, password);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo entrar");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: "1.2rem" }}>Panel de plataforma</h1>
          <p className="suave pequeno" style={{ margin: "4px 0 0" }}>
            Acceso del proveedor, no de los clubes
          </p>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        <Campo etiqueta="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />
        </Campo>
        <Campo etiqueta="Contraseña">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </Campo>

        <button className="boton grande bloque" disabled={enviando} style={{ marginTop: 6 }}>
          {enviando ? "Entrando..." : "Entrar"}
        </button>

        <p className="centrado pequeno" style={{ margin: "10px 0 0" }}>
          <button type="button" className="boton enlace" onClick={() => setRecuperando(true)}>
            He olvidado la contraseña
          </button>
        </p>
      </form>
    </div>
  );
}

function PedirEnlace({ alVolver }: { alVolver: () => void }) {
  const [email, setEmail] = useState("");
  const [enviado, setEnviado] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      const r = await api<{ mensaje: string }>("/plataforma/auth/recuperar", {
        metodo: "POST",
        cuerpo: { email },
      });
      setEnviado(r.mensaje);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo enviar el correo");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: "1.2rem" }}>He olvidado la contraseña</h1>
          <p className="suave pequeno" style={{ margin: "4px 0 0" }}>
            Del panel de plataforma, no de un club
          </p>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        {enviado ? (
          <>
            <Aviso tipo="ok">{enviado}</Aviso>
            <p className="suave pequeno">Mira también en el correo no deseado. El enlace caduca en una hora.</p>
          </>
        ) : (
          <>
            <Campo etiqueta="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </Campo>
            <button className="boton grande bloque" disabled={enviando} style={{ marginTop: 6 }}>
              {enviando ? "Enviando..." : "Enviarme el enlace"}
            </button>
          </>
        )}

        <p className="centrado pequeno" style={{ margin: "10px 0 0" }}>
          <button type="button" className="boton enlace" onClick={alVolver}>
            Volver a la entrada
          </button>
        </p>
      </form>
    </div>
  );
}

function PonerNueva({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const coinciden = password.length > 0 && password === repetida;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await api("/plataforma/auth/restablecer", { metodo: "POST", cuerpo: { token, password } });
      setListo(true);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo cambiar la contraseña");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="centrado" style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: "1.2rem" }}>Nueva contraseña</h1>
        </div>

        <Aviso tipo="error">{error}</Aviso>

        {listo ? (
          <>
            <Aviso tipo="ok">Contraseña cambiada.</Aviso>
            <a className="boton grande bloque" href="/plataforma">
              Entrar
            </a>
          </>
        ) : (
          <>
            <Campo etiqueta="Contraseña nueva" ayuda="Mínimo 10 caracteres: esta cuenta ve todos los clubes.">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                autoFocus
              />
            </Campo>
            <Campo etiqueta="Repitela">
              <input
                type="password"
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Campo>

            {repetida.length > 0 && !coinciden && <Aviso tipo="error">Las dos contraseñas no coinciden.</Aviso>}

            <button
              className="boton grande bloque"
              disabled={enviando || password.length < 10 || !coinciden}
              style={{ marginTop: 6 }}
            >
              {enviando ? "Guardando..." : "Cambiar la contraseña"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

function DialogoPassword({
  alCerrar,
  alHecho,
  alFallar,
}: {
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [enviando, setEnviando] = useState(false);

  const coinciden = nueva.length > 0 && nueva === repetida;
  const listo = actual.length > 0 && nueva.length >= 10 && coinciden;

  return (
    <Modal
      titulo="Cambiar mi contraseña"
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !listo}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/plataforma/auth/cambiar-password", {
                  metodo: "POST",
                  cuerpo: { actual, nueva },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : "No se pudo cambiar la contraseña");
              } finally {
                setEnviando(false);
              }
            }}
          >
            {enviando ? "Guardando..." : "Cambiar"}
          </button>
        </>
      }
    >
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        Esta cuenta ve la lista de todos los clubes. Usa una contraseña larga y que no uses en ningún otro sitio.
      </p>

      <Campo etiqueta="Contraseña actual">
        <input
          type="password"
          value={actual}
          onChange={(e) => setActual(e.target.value)}
          autoComplete="current-password"
          autoFocus
        />
      </Campo>
      <Campo etiqueta="Contraseña nueva" ayuda="Mínimo 10 caracteres.">
        <input type="password" value={nueva} onChange={(e) => setNueva(e.target.value)} autoComplete="new-password" />
      </Campo>
      <Campo etiqueta="Repite la nueva">
        <input
          type="password"
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
          autoComplete="new-password"
        />
      </Campo>

      {repetida.length > 0 && !coinciden && <Aviso tipo="error">Las dos contraseñas nuevas no coinciden.</Aviso>}
    </Modal>
  );
}

function DialogoNuevoClub({
  diasDePrueba,
  alCerrar,
  alHecho,
  alFallar,
}: {
  diasDePrueba: number;
  alCerrar: () => void;
  alHecho: (nombre: string) => void;
  alFallar: (mensaje: string) => void;
}) {
  const [datos, setDatos] = useState({
    nombreClub: "",
    ciudad: "",
    nombre: "",
    email: "",
    password: "",
    diasPrueba: String(diasDePrueba),
    cuotaMensualEuros: "",
  });
  const [enviando, setEnviando] = useState(false);
  const cambiar = (campo: string, valor: string) => setDatos((d) => ({ ...d, [campo]: valor }));

  return (
    <Modal
      titulo="Dar de alta un club"
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !datos.nombreClub || !datos.nombre || !datos.email || datos.password.length < 8}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/plataforma/clubes", {
                  metodo: "POST",
                  cuerpo: {
                    nombreClub: datos.nombreClub,
                    ciudad: datos.ciudad || null,
                    nombre: datos.nombre,
                    email: datos.email,
                    password: datos.password,
                    diasPrueba: Number(datos.diasPrueba || 0),
                    cuotaMensualEuros: Number(datos.cuotaMensualEuros || 0),
                  },
                });
                alHecho(datos.nombreClub);
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : "No se pudo crear el club");
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            Crear
          </button>
        </>
      }
    >
      <Campo etiqueta="Nombre de la asociación">
        <input value={datos.nombreClub} onChange={(e) => cambiar("nombreClub", e.target.value)} autoFocus />
      </Campo>
      <Campo etiqueta="Ciudad">
        <input value={datos.ciudad} onChange={(e) => cambiar("ciudad", e.target.value)} />
      </Campo>

      <div className="rotulo" style={{ margin: "10px 0 6px" }}>
        Cuenta del responsable
      </div>
      <Campo etiqueta="Nombre">
        <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} />
      </Campo>
      <Campo etiqueta="Email" ayuda="Sera su usuario para entrar.">
        <input type="email" value={datos.email} onChange={(e) => cambiar("email", e.target.value)} />
      </Campo>
      <Campo etiqueta="Contraseña inicial" ayuda="Mínimo 8 caracteres. Diles que la cambien al entrar.">
        <input value={datos.password} onChange={(e) => cambiar("password", e.target.value)} />
      </Campo>

      <div className="linea-campos">
        <Campo etiqueta="Días de prueba" ayuda="0 para empezar a cobrar ya.">
          <input type="number" min={0} max={365} value={datos.diasPrueba} onChange={(e) => cambiar("diasPrueba", e.target.value)} />
        </Campo>
        <Campo etiqueta="Cuota mensual (EUR)">
          <input type="number" min={0} step="0.01" value={datos.cuotaMensualEuros} onChange={(e) => cambiar("cuotaMensualEuros", e.target.value)} />
        </Campo>
      </div>
    </Modal>
  );
}

function DialogoClub({
  club,
  alCerrar,
  alHecho,
  alFallar,
  alRefrescar,
}: {
  club: Club;
  alCerrar: () => void;
  alRefrescar: () => void;
  alHecho: (mensaje: string) => void;
  alFallar: (mensaje: string) => void;
}) {
  const [cuota, setCuota] = useState(String(club.cuotaMensualCentimos / 100));
  const [prueba, setPrueba] = useState(club.pruebaHasta?.slice(0, 10) ?? "");
  const [notas, setNotas] = useState(club.notasPlataforma ?? "");
  const [motivo, setMotivo] = useState("");
  const [confirmacion, setConfirmacion] = useState("");
  const [borrando, setBorrando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  const uso = useQuery({
    queryKey: ["plataforma-uso", club.id],
    queryFn: () =>
      api<{
        usuariosActivos: number;
        productosActivos: number;
        ultimoAccesoEquipo: string | null;
        entregasPorMes: { mes: string; total: number }[];
      }>(`/plataforma/clubes/${club.id}/uso`),
  });

  async function ejecutar(accion: () => Promise<unknown>, mensaje: string) {
    setTrabajando(true);
    try {
      await accion();
      alHecho(mensaje);
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : "No se pudo completar la operación");
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={club.nombre}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cerrar
          </button>
          <button
            className="boton"
            disabled={trabajando}
            onClick={() =>
              ejecutar(
                () =>
                  api(`/plataforma/clubes/${club.id}`, {
                    metodo: "PATCH",
                    cuerpo: {
                      cuotaMensualEuros: Number(cuota || 0),
                      pruebaHasta: prueba || null,
                      notasPlataforma: notas || null,
                    },
                  }),
                `Suscripcion de «${club.nombre}» actualizada.`,
              )
            }
          >
            Guardar
          </button>
        </>
      }
    >
      <div className="rejilla tres" style={{ marginBottom: 14 }}>
        <div>
          <div className="rotulo">Socios</div>
          <div className="valor" style={{ fontSize: "1.3rem" }}>
            {club.sociosActivos} <span className="suave pequeno">de {club.sociosTotales}</span>
          </div>
          <div
            className="pequeno"
            style={{ color: club.plan.desajuste === "COBRA_DE_MENOS" ? "var(--rojo)" : "var(--suave)" }}
          >
            {club.plan.tramo}
            {club.plan.precioQueTocaCentimos !== null && ` · ${euros(club.plan.precioQueTocaCentimos)}/mes`}
          </div>
        </div>
        <div>
          <div className="rotulo">Entregas 30 d</div>
          <div className="valor" style={{ fontSize: "1.3rem" }}>{club.entregas30Dias}</div>
        </div>
        <div>
          <div className="rotulo">Equipo</div>
          <div className="valor" style={{ fontSize: "1.3rem" }}>{uso.data?.usuariosActivos ?? "—"}</div>
        </div>
      </div>

      <p className="suave pequeno" style={{ marginTop: 0 }}>
        Alta el {fecha(club.createdAt)} · identificador <strong>{club.slug}</strong>
        {club.email ? ` · ${club.email}` : ""}
        <br />
        Ultimo acceso del equipo: {uso.data?.ultimoAccesoEquipo ? fechaHora(uso.data.ultimoAccesoEquipo) : "nunca"}
      </p>

      {uso.data && uso.data.entregasPorMes.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="rotulo" style={{ marginBottom: 6 }}>Entregas por mes</div>
          <div className="columna" style={{ gap: 6 }}>
            {uso.data.entregasPorMes.map((m) => {
              const mayor = Math.max(...uso.data!.entregasPorMes.map((x) => x.total), 1);
              return (
                <div key={m.mes}>
                  <div className="fila separada pequeno">
                    <span className="suave">{m.mes}</span>
                    <span className="tabular">{m.total}</span>
                  </div>
                  <div className="barra">
                    <span style={{ width: `${(m.total / mayor) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="linea-campos">
        <Campo etiqueta="Cuota mensual (EUR)">
          <input type="number" min={0} step="0.01" value={cuota} onChange={(e) => setCuota(e.target.value)} />
        </Campo>
        <Campo etiqueta="Prueba hasta" ayuda="Vacío = sin prueba, se considera al día.">
          <input type="date" value={prueba} onChange={(e) => setPrueba(e.target.value)} />
        </Campo>
      </div>

      <Campo etiqueta="Notas internas" ayuda="El club no las ve.">
        <textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} />
      </Campo>

      <div className="columna" style={{ gap: 12, margin: "14px 0" }}>
        <CobrosDelClub
          clubId={club.id}
          cuotaMensualCentimos={club.cuotaMensualCentimos}
          pagadoHasta={club.pagadoHasta}
          alCobrar={alRefrescar}
        />
        <EquipoDelClub clubId={club.id} />
      </div>

      <ConfiguracionDelClub clubId={club.id} />
      <CopiasDelClub clubId={club.id} />
      <HistorialDelClub clubId={club.id} />

      <div style={{ borderTop: "1px solid var(--borde)", paddingTop: 14, marginTop: 4 }}>
        {club.activo ? (
          <>
            <div className="rotulo" style={{ marginBottom: 6 }}>Suspender</div>
            <p className="ayuda" style={{ marginTop: 0 }}>
              Su equipo y sus socios quedan fuera al instante y la carta deja de servirse. Los datos se conservan.
            </p>
            <Campo etiqueta="Motivo">
              <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Impago de octubre" />
            </Campo>
            <button
              className="boton peligro"
              disabled={trabajando || motivo.trim().length < 3}
              onClick={() =>
                ejecutar(
                  () => api(`/plataforma/clubes/${club.id}/suspender`, { metodo: "POST", cuerpo: { motivo } }),
                  `«${club.nombre}» suspendido.`,
                )
              }
            >
              Suspender el club
            </button>
          </>
        ) : (
          <>
            <Aviso tipo="atencion">
              Suspendido{club.motivoSuspension ? `: ${club.motivoSuspension}` : ""}
            </Aviso>
            <div className="fila ajustable">
              <button
                className="boton"
                disabled={trabajando}
                onClick={() =>
                  ejecutar(
                    () => api(`/plataforma/clubes/${club.id}/reactivar`, { metodo: "POST" }),
                    `«${club.nombre}» reactivado.`,
                  )
                }
              >
                Reactivar
              </button>
              <button className="boton fantasma" onClick={() => setBorrando(!borrando)} style={{ color: "var(--rojo)" }}>
                Borrar definitivamente
              </button>
            </div>

            {borrando && (
              <div style={{ marginTop: 12 }}>
                <Aviso tipo="error">
                  Esto borra el club y <strong>todos sus datos</strong>: socios, dispensaciones, caja e historial. No
                  hay vuelta atrás, y tú no puedes recuperarlo: la copia la descarga el club desde sus Ajustes, no
                  este panel.
                </Aviso>
                <Campo etiqueta={`Escribe «${club.slug}» para confirmar`}>
                  <input value={confirmacion} onChange={(e) => setConfirmacion(e.target.value)} />
                </Campo>
                <button
                  className="boton peligro"
                  disabled={trabajando || confirmacion !== club.slug}
                  onClick={() =>
                    ejecutar(
                      () => api(`/plataforma/clubes/${club.id}`, { metodo: "DELETE", cuerpo: { confirmacion } }),
                      `«${club.nombre}» borrado.`,
                    )
                  }
                >
                  Borrar para siempre
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

interface EstadoCorreo {
  ok: boolean;
  error?: string;
  via: "api" | "smtp" | null;
  host: string | null;
  puerto: number;
  usuario: string | null;
  desde: string;
  urlPublica: string;
}

/**
 * Como esta el correo del servidor.
 *
 * Hace falta porque el fallo no se ve desde fuera: las pantallas de
 * recuperacion contestan lo mismo salga o no salga el correo -a proposito, para
 * no chivar que direcciones tienen cuenta-, asi que «no me llega» puede ser una
 * variable que falta, un puerto mal puesto o la carpeta de spam, y no hay forma
 * de distinguirlo sin preguntar.
 */
function DialogoCorreo({ alCerrar }: { alCerrar: () => void }) {
  const estado = useQuery({
    queryKey: ["plataforma-correo"],
    queryFn: () => api<EstadoCorreo>("/plataforma/correo"),
    retry: false,
  });

  const d = estado.data;
  const enlacesRotos = Boolean(d && /localhost/.test(d.urlPublica));

  return (
    <Modal
      titulo="Correo del servidor"
      alCerrar={alCerrar}
      acciones={
        <button className="boton" onClick={alCerrar}>
          Cerrar
        </button>
      }
    >
      {estado.isLoading ? (
        <Cargando texto="Hablando con el servidor de correo..." />
      ) : !d ? (
        <Aviso tipo="error">No se pudo comprobar.</Aviso>
      ) : (
        <>
          {d.ok ? (
            <Aviso tipo="ok">El servidor de correo contesta. Los correos salen.</Aviso>
          ) : (
            <Aviso tipo="error">{d.error}</Aviso>
          )}

          {!d.ok && d.via === "smtp" && /ETIMEDOUT|ESOCKET|ECONNREFUSED/.test(d.error ?? "") && (
            <Aviso tipo="atencion">
              Si ya has probado los puertos 587, 465 y 2525, es que el hospedaje no deja salir correo por SMTP: lo
              cierran para que no se use para spam. La salida es mandar por HTTPS: pon la variable BREVO_API_KEY con
              una clave de la API de Brevo y el programa la usará en lugar del SMTP.
            </Aviso>
          )}

          {enlacesRotos && (
            <Aviso tipo="atencion">
              Los enlaces de los correos apuntan a {d.urlPublica}, que solo vale en tu ordenador. Pon la variable
              URL_PUBLICA en el servidor con la dirección de verdad.
            </Aviso>
          )}

          <table className="tabla">
            <tbody>
              <tr>
                <td className="suave pequeno">Sale por</td>
                <td>{d.via === "api" ? "HTTPS (API de Brevo)" : d.via === "smtp" ? "SMTP" : "nada, sin configurar"}</td>
              </tr>
              <tr>
                <td className="suave pequeno">Servidor</td>
                <td>{d.host ? `${d.host}:${d.puerto}` : <span className="suave">sin poner</span>}</td>
              </tr>
              {d.via !== "api" && (
                <tr>
                  <td className="suave pequeno">Usuario</td>
                  <td>{d.usuario ?? <span className="suave">sin poner</span>}</td>
                </tr>
              )}
              <tr>
                <td className="suave pequeno">Remitente</td>
                <td>{d.desde}</td>
              </tr>
              <tr>
                <td className="suave pequeno">Enlaces</td>
                <td>{d.urlPublica}</td>
              </tr>
            </tbody>
          </table>

          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            Esto solo comprueba que se puede hablar con el servidor de correo. Que el correo llegue a la bandeja y no
            a spam depende del dominio del remitente.
          </p>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Registro, copias y configuración: lo que hace falta cuando un club llama.
// ---------------------------------------------------------------------------

interface Apunte {
  id: string;
  adminNombre: string;
  accion: string;
  clubNombre: string | null;
  clubSlug: string | null;
  datos: Record<string, unknown> | null;
  fecha: string;
}

const ACCIONES: Record<string, string> = {
  CREAR_CLUB: "Dio de alta el club",
  EDITAR_CLUB: "Cambió los datos del club",
  SUSPENDER_CLUB: "Suspendió el club",
  REACTIVAR_CLUB: "Reactivó el club",
  BORRAR_CLUB: "Borró el club",
  MANDAR_ACCESO: "Mandó un enlace de acceso",
  APUNTAR_COBRO: "Apuntó un cobro",
  GENERAR_LICENCIAS: "Generó licencias",
  ANULAR_LICENCIA: "Anuló una licencia",
  CAMBIAR_PASSWORD: "Cambió su contraseña",
};

/** Lo que trae el apunte, en una línea que se lea. */
function detalleDelApunte(a: Apunte): string {
  const d = a.datos;
  if (!d) return "";
  if (a.accion === "SUSPENDER_CLUB") return String(d.motivo ?? "");
  if (a.accion === "BORRAR_CLUB") {
    return [
      plural(Number(d.socios), "socio"),
      plural(Number(d.dispensaciones), "dispensación", "dispensaciones"),
      `${d.usuarios} del equipo`,
    ].join(" · ");
  }
  if (a.accion === "APUNTAR_COBRO") {
    return [euros(Math.round(Number(d.importeEuros) * 100)), plural(Number(d.meses), "mes", "meses"), String(d.metodo).toLowerCase()].join(" · ");
  }
  if (a.accion === "MANDAR_ACCESO") return String(d.a ?? "");
  if (a.accion === "GENERAR_LICENCIAS") return plural(Number(d.cantidad), "código", "códigos");
  if (a.accion === "ANULAR_LICENCIA") return String(d.codigo ?? "");
  if (a.accion === "EDITAR_CLUB") {
    const trozos: string[] = [];
    if (d.cuotaAhora !== undefined) {
      trozos.push(`cuota ${euros(Math.round(Number(d.cuotaAntes) * 100))} → ${euros(Math.round(Number(d.cuotaAhora) * 100))}`);
    }
    if (d.pruebaAhora !== undefined) trozos.push("prueba movida");
    if (d.notas) trozos.push("notas");
    return trozos.join(" · ");
  }
  return "";
}

function useRegistro(clubId?: string) {
  return useQuery({
    queryKey: ["plataforma-registro", clubId ?? "todo"],
    queryFn: () =>
      api<Apunte[]>("/plataforma/registro", { query: clubId ? { clubId } : {} }),
  });
}

function TablaDeApuntes({ apuntes, conClub }: { apuntes: Apunte[]; conClub: boolean }) {
  return (
    <div className="tabla-envoltorio">
      <table className="tabla">
        <thead>
          <tr>
            <th>Cuándo</th>
            <th>Quién</th>
            {conClub && <th>Club</th>}
            <th>Qué hizo</th>
          </tr>
        </thead>
        <tbody>
          {apuntes.map((a) => (
            <tr key={a.id}>
              <td className="pequeno suave">{fechaHora(a.fecha)}</td>
              <td className="pequeno">{a.adminNombre}</td>
              {conClub && <td className="pequeno">{a.clubNombre ?? <span className="suave">—</span>}</td>}
              <td className="pequeno">
                {ACCIONES[a.accion] ?? a.accion}
                {detalleDelApunte(a) && <div className="suave">{detalleDelApunte(a)}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Todo lo que se ha hecho desde este panel.
 *
 * Los apuntes de un club borrado siguen aquí a propósito: el día que hay que
 * explicar qué pasó con un club, ese es justo el que ya no está para contarlo.
 */
function Registro() {
  const registro = useRegistro();

  if (registro.isLoading) return <Cargando />;
  const apuntes = registro.data ?? [];

  return (
    <Panel titulo="Lo que se ha hecho desde aquí" sinCuerpo>
      {registro.isError ? (
        /* Un fallo pintado como «no hay nada» es peor que el fallo: dice que no
           ha pasado nada, cuando lo que pasa es que no se sabe. */
        <Vacio>No se pudo leer el registro. Vuelve a intentarlo en un momento.</Vacio>
      ) : apuntes.length === 0 ? (
        <Vacio>Todavía no hay nada apuntado.</Vacio>
      ) : (
        <TablaDeApuntes apuntes={apuntes} conClub />
      )}
    </Panel>
  );
}

function HistorialDelClub({ clubId }: { clubId: string }) {
  const registro = useRegistro(clubId);
  const apuntes = registro.data ?? [];
  if (apuntes.length === 0) return null;

  return (
    <Panel titulo="Historial de este club" sinCuerpo>
      <TablaDeApuntes apuntes={apuntes} conClub={false} />
    </Panel>
  );
}

/**
 * Que el club tiene copias, sin abrir ninguna.
 *
 * Solo fechas y tamaños. Una copia lleva dentro a todos los socios, y este
 * panel promete no enseñar eso; lo que hacía falta era no borrar un club a
 * ciegas sin saber si hay de dónde tirar.
 */
function CopiasDelClub({ clubId }: { clubId: string }) {
  const copias = useQuery({
    queryKey: ["plataforma-copias", clubId],
    queryFn: () =>
      api<{ copias: { nombre: string; fecha: string; bytes: number }[]; ultima: string | null }>(
        `/plataforma/clubes/${clubId}/copias`,
      ),
  });

  const d = copias.data;

  return (
    <Panel titulo="Copias de seguridad">
      {copias.isLoading ? (
        <Cargando />
      ) : !d || d.copias.length === 0 ? (
        <Aviso tipo="atencion">
          Este club no tiene ninguna copia automática todavía. Si va a borrarse algo, que la descarguen antes desde
          sus Ajustes.
        </Aviso>
      ) : (
        <>
          <p style={{ marginTop: 0 }}>
            <strong>{d.copias.length}</strong> {d.copias.length === 1 ? "copia guardada" : "copias guardadas"}. La más
            reciente es de {fechaHora(d.ultima!)}.
          </p>
          <p className="ayuda" style={{ marginBottom: 0 }}>
            Solo ves que existen. Descargarlas es cosa del club, desde Ajustes → Copia de seguridad: dentro va el
            fichero de socios entero, y este panel no lo enseña.
          </p>
        </>
      )}
    </Panel>
  );
}

interface Configuracion {
  limiteDiarioG: number;
  limiteMensualG: number;
  cuotaAnualEuros: number;
  aceptoCondiciones: string | null;
  tieneLogo: boolean;
  tieneCif: boolean;
  ciudad: string | null;
  tiposSocio: {
    nombre: string;
    cuotaAnualEuros: number | null;
    descuentoPorcentaje: number;
    duracionMeses: number;
    limiteDiarioG: number | null;
  }[];
  categorias: number;
  productos: number;
  productosActivos: number;
  puestos: number;
  descuentosActivos: number;
  cartas: number;
  versionesDeContrato: number;
}

/**
 * Cómo tiene el club montado el programa.
 *
 * La mitad de las llamadas son «no me deja dispensar», y la respuesta está casi
 * siempre en sus límites o en sus tipos de socio. Son ajustes, no personas.
 */
function ConfiguracionDelClub({ clubId }: { clubId: string }) {
  const conf = useQuery({
    queryKey: ["plataforma-configuracion", clubId],
    queryFn: () => api<Configuracion>(`/plataforma/clubes/${clubId}/configuracion`),
  });

  if (conf.isLoading) return <Panel titulo="Cómo lo tienen montado"><Cargando /></Panel>;
  const c = conf.data;
  if (!c) return null;

  return (
    <Panel titulo="Cómo lo tienen montado" sinCuerpo>
      <div className="tabla-envoltorio">
        <table className="tabla">
          <tbody>
            <tr>
              <td className="suave pequeno">Límite por socio</td>
              <td>
                {c.limiteDiarioG} g al día · {c.limiteMensualG} g al mes
              </td>
            </tr>
            <tr>
              <td className="suave pequeno">Cuota por defecto</td>
              <td>{c.cuotaAnualEuros} €</td>
            </tr>
            <tr>
              <td className="suave pequeno">Catálogo</td>
              <td>
                {c.productosActivos} de {plural(c.productos, "producto")} activos ·{" "}
                {plural(c.categorias, "categoría", "categorías")}
                {c.descuentosActivos > 0 && ` · ${plural(c.descuentosActivos, "descuento")}`}
              </td>
            </tr>
            <tr>
              <td className="suave pequeno">Tienen montado</td>
              <td className="pequeno">
                {[
                  c.cartas > 0 && plural(c.cartas, "carta"),
                  c.puestos > 0 && plural(c.puestos, "puesto"),
                  c.versionesDeContrato > 0 && "contrato",
                  c.tieneLogo && "logo",
                  c.tieneCif && "CIF",
                ]
                  .filter(Boolean)
                  .join(" · ") || <span className="suave">Nada más que lo básico</span>}
              </td>
            </tr>
            {c.tiposSocio.length > 0 && (
              <tr>
                <td className="suave pequeno">Tipos de socio</td>
                <td className="pequeno">
                  {c.tiposSocio.map((t) => (
                    <div key={t.nombre}>
                      <strong>{t.nombre}</strong>{" "}
                      <span className="suave">
                        {t.cuotaAnualEuros === null ? "cuota del club" : `${t.cuotaAnualEuros} €`} ·{" "}
                        {plural(t.duracionMeses, "mes", "meses")}
                        {t.descuentoPorcentaje > 0 && ` · −${t.descuentoPorcentaje}%`}
                        {t.limiteDiarioG !== null && ` · ${t.limiteDiarioG} g/día`}
                      </span>
                    </div>
                  ))}
                </td>
              </tr>
            )}
            <tr>
              <td className="suave pequeno">Condiciones</td>
              <td className="pequeno">
                {c.aceptoCondiciones ? (
                  `Aceptadas el ${fecha(c.aceptoCondiciones)}`
                ) : (
                  <span className="suave">Sin constancia</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="ayuda" style={{ padding: "10px 14px", margin: 0 }}>
        Ajustes del club, nunca socios: aquí no hay ni un nombre, ni un documento, ni un consumo.
      </p>
    </Panel>
  );
}

interface Revision {
  club: { nombre: string; slug: string };
  version: number;
  generada: string;
  filas: number;
  porTabla: Record<string, number>;
  mismoClubExiste: boolean;
  slugOcupado: boolean;
  avisos: string[];
}

/**
 * Restaurar una copia.
 *
 * Se mira antes de meter: el fichero dice de qué club es, de cuándo, cuántas
 * filas trae y qué no lleva. Nadie debería descubrir el contenido de una copia
 * después de haberla restaurado.
 */
function DialogoRestaurar({ alCerrar, alHecho }: { alCerrar: () => void; alHecho: (mensaje: string) => void }) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [revision, setRevision] = useState<Revision | null>(null);
  const [error, setError] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  async function mandar(ruta: string) {
    if (!archivo) return;
    setTrabajando(true);
    setError("");
    try {
      // Diez minutos: restaurar un club de años son decenas de miles de filas.
      return await subirArchivo(ruta, archivo, 10 * 60 * 1000);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo leer la copia");
      return null;
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo="Restaurar una copia"
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          {revision && (
            <button
              className="boton"
              disabled={trabajando || revision.mismoClubExiste}
              onClick={async () => {
                const r = (await mandar("/plataforma/restaurar")) as { nombre: string; filas: number } | null;
                if (r) alHecho(`«${r.nombre}» restaurado: ${r.filas} filas. Queda suspendido hasta que lo reactives.`);
              }}
            >
              {trabajando ? "Restaurando..." : "Restaurar este club"}
            </button>
          )}
        </>
      }
    >
      <Aviso tipo="error">{error}</Aviso>

      <p style={{ marginTop: 0 }}>
        Vale el <code>.json</code> que descarga el club desde sus Ajustes y también el <code>.json.gz</code> que
        guarda el servidor cada noche.
      </p>

      <Campo etiqueta="Fichero de copia">
        <input
          type="file"
          accept=".json,.gz,application/json,application/gzip"
          onChange={(e) => {
            setArchivo(e.target.files?.[0] ?? null);
            setRevision(null);
            setError("");
          }}
        />
      </Campo>

      {archivo && !revision && (
        <button
          className="boton secundario"
          disabled={trabajando}
          onClick={async () => {
            const r = (await mandar("/plataforma/restaurar/revisar")) as Revision | null;
            if (r) setRevision(r);
          }}
        >
          {trabajando ? "Mirando..." : "Ver qué lleva"}
        </button>
      )}

      {revision && (
        <>
          <div className="tabla-envoltorio" style={{ marginTop: 12 }}>
            <table className="tabla">
              <tbody>
                <tr>
                  <td className="suave pequeno">Club</td>
                  <td>
                    <strong>{revision.club.nombre}</strong> <span className="suave">{revision.club.slug}</span>
                  </td>
                </tr>
                <tr>
                  <td className="suave pequeno">Copia</td>
                  <td>
                    Del {fechaHora(revision.generada)} · versión {revision.version}
                  </td>
                </tr>
                <tr>
                  <td className="suave pequeno">Trae</td>
                  <td>
                    <strong>{revision.filas}</strong> filas ·{" "}
                    <span className="suave">
                      {Object.entries(revision.porTabla)
                        .map(([t, n]) => `${t} ${n}`)
                        .join(" · ")}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {revision.mismoClubExiste ? (
            <Aviso tipo="error">
              Ese club sigue existiendo en el servidor, así que esta copia no se puede restaurar: escribir encima
              borraría lo que hay ahora. Si de verdad quieres reemplazarlo, bórralo antes desde el panel.
            </Aviso>
          ) : (
            revision.slugOcupado && (
              <Aviso tipo="atencion">
                El identificador «{revision.club.slug}» está pillado por otro club. El restaurado se creará con uno
                distinto; nunca se escribe encima de lo que ya está.
              </Aviso>
            )
          )}

          <ul className="ayuda" style={{ paddingLeft: "1.2em", marginBottom: 0 }}>
            {revision.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
